import { tenantApi } from '../api/tenantApi';
import { baseApi } from '../store/baseApi';
import { store } from '../store';
import { setPendingTaskCount } from '../store/authSlice';

/** RTK util helpers — endpoint names span tenantApi + codegen injects on baseApi. */
type AnyApiUtil = {
  selectCachedArgsForQuery: (state: unknown, endpoint: string) => unknown[];
  updateQueryData: (
    endpoint: string,
    args: unknown,
    updateRecipe: (draft: Record<string, unknown>) => void,
  ) => unknown;
};

const apiUtil = baseApi.util as unknown as AnyApiUtil;

export type OrgDataChangedPayload = {
  orgId?: number;
  entityType?: string;
  entityId?: number | string | null;
  action?: string;
  tags?: string[];
  record?: unknown | null;
};

type ListEndpoint = { endpoint: string; dataKey: string };
type DetailEndpoint = { endpoint: string; dataKey: string };

type EntityCacheConfig = {
  lists?: ListEndpoint[];
  details?: DetailEndpoint[];
  /** Endpoints that return a bare array under dataKey (e.g. myTasks). */
  arrays?: ListEndpoint[];
  tags: string[];
};

const ENTITY_CACHE: Record<string, EntityCacheConfig> = {
  customer: {
    tags: ['Customer'],
    lists: [
      { endpoint: 'listCustomers', dataKey: 'listCustomers' },
      { endpoint: 'ListCustomers', dataKey: 'listCustomers' },
    ],
    details: [
      { endpoint: 'getCustomer', dataKey: 'getCustomer' },
      { endpoint: 'GetCustomer', dataKey: 'getCustomer' },
    ],
  },
  vendor: {
    tags: ['Vendor'],
    lists: [
      { endpoint: 'listVendors', dataKey: 'listVendors' },
      { endpoint: 'ListVendors', dataKey: 'listVendors' },
    ],
    details: [
      { endpoint: 'getVendor', dataKey: 'getVendor' },
      { endpoint: 'GetVendor', dataKey: 'getVendor' },
    ],
  },
  product: {
    tags: ['Product'],
    lists: [
      { endpoint: 'listProducts', dataKey: 'listProducts' },
      { endpoint: 'ListProducts', dataKey: 'listProducts' },
    ],
    details: [
      { endpoint: 'getProduct', dataKey: 'getProduct' },
      { endpoint: 'GetProduct', dataKey: 'getProduct' },
    ],
  },
  warehouse: {
    tags: ['Warehouse'],
    lists: [
      { endpoint: 'listWarehouses', dataKey: 'listWarehouses' },
      { endpoint: 'ListWarehouses', dataKey: 'listWarehouses' },
    ],
  },
  labour: {
    tags: ['Labour'],
    lists: [
      { endpoint: 'listLabour', dataKey: 'listLabour' },
      { endpoint: 'ListLabour', dataKey: 'listLabour' },
    ],
  },
  transport: {
    tags: ['Transport'],
    lists: [
      { endpoint: 'listTransport', dataKey: 'listTransport' },
      { endpoint: 'ListTransport', dataKey: 'listTransport' },
    ],
  },
  chamber: {
    tags: ['Chamber'],
    lists: [{ endpoint: 'listChambers', dataKey: 'listChambers' }],
  },
  floor: {
    tags: ['Floor'],
    lists: [{ endpoint: 'listFloors', dataKey: 'listFloors' }],
  },
  rack: {
    tags: ['Rack'],
    lists: [{ endpoint: 'listRacks', dataKey: 'listRacks' }],
  },
  staff: {
    tags: ['Staff'],
    lists: [{ endpoint: 'listStaff', dataKey: 'listStaff' }],
  },
  procurement: {
    tags: ['Procurement'],
    lists: [
      { endpoint: 'listProcurement', dataKey: 'listProcurement' },
      { endpoint: 'ListProcurement', dataKey: 'listProcurement' },
    ],
    details: [
      { endpoint: 'getProcurement', dataKey: 'getProcurement' },
      { endpoint: 'GetProcurement', dataKey: 'getProcurement' },
    ],
  },
  proc: {
    tags: ['Procurement'],
    lists: [
      { endpoint: 'listProcurement', dataKey: 'listProcurement' },
      { endpoint: 'ListProcurement', dataKey: 'listProcurement' },
    ],
    details: [
      { endpoint: 'getProcurement', dataKey: 'getProcurement' },
      { endpoint: 'GetProcurement', dataKey: 'getProcurement' },
    ],
  },
  task: {
    tags: ['Task'],
    arrays: [{ endpoint: 'myTasks', dataKey: 'myTasks' }],
  },
  inventorytransfer: {
    tags: ['InventoryTransfer', 'Product'],
    lists: [
      { endpoint: 'listInventoryTransfers', dataKey: 'listInventoryTransfers' },
      { endpoint: 'ListInventoryTransfers', dataKey: 'listInventoryTransfers' },
    ],
  },
  'inventory-transfer': {
    tags: ['InventoryTransfer', 'Product'],
    lists: [
      { endpoint: 'listInventoryTransfers', dataKey: 'listInventoryTransfers' },
      { endpoint: 'ListInventoryTransfers', dataKey: 'listInventoryTransfers' },
    ],
  },
  role: {
    tags: ['Staff'],
    arrays: [
      { endpoint: 'listRoles', dataKey: 'listRoles' },
      { endpoint: 'ListRoles', dataKey: 'listRoles' },
    ],
  },
  approvalflowdefinition: {
    tags: ['Staff'],
    arrays: [
      { endpoint: 'listApprovalFlowDefinitions', dataKey: 'listApprovalFlowDefinitions' },
      { endpoint: 'ListApprovalFlowDefinitions', dataKey: 'listApprovalFlowDefinitions' },
    ],
  },
  'approval-flow-definition': {
    tags: ['Staff'],
    arrays: [
      { endpoint: 'listApprovalFlowDefinitions', dataKey: 'listApprovalFlowDefinitions' },
      { endpoint: 'ListApprovalFlowDefinitions', dataKey: 'listApprovalFlowDefinitions' },
    ],
  },
  systemnotification: {
    tags: ['SystemNotification'],
    arrays: [
      { endpoint: 'SystemNotifications', dataKey: 'systemNotifications' },
      { endpoint: 'systemNotifications', dataKey: 'systemNotifications' },
    ],
  },
  'system-notification': {
    tags: ['SystemNotification'],
    arrays: [
      { endpoint: 'SystemNotifications', dataKey: 'systemNotifications' },
      { endpoint: 'systemNotifications', dataKey: 'systemNotifications' },
    ],
  },
};

const KNOWN_TAGS = new Set([
  'Customer',
  'Procurement',
  'Tenant',
  'Vendor',
  'Product',
  'Warehouse',
  'Chamber',
  'Floor',
  'Rack',
  'Labour',
  'Transport',
  'Staff',
  'Task',
  'SystemNotification',
  'InventoryTransfer',
  'Billing',
]);

function rowId(row: unknown): string {
  if (row == null || typeof row !== 'object') return '';
  const id = (row as { id?: unknown }).id;
  return id == null ? '' : String(id);
}

function normalizeAction(action?: string | null): 'ADD' | 'UPDATE' | 'DELETE' | null {
  if (!action) return null;
  const upper = String(action).toUpperCase();
  if (upper === 'ADD' || upper === 'CREATE') return 'ADD';
  if (upper === 'DELETE' || upper === 'REMOVE') return 'DELETE';
  return 'UPDATE';
}

function argMatchesEntityId(arg: unknown, entityId: string): boolean {
  if (arg == null) return false;
  if (typeof arg === 'string' || typeof arg === 'number') return String(arg) === entityId;
  if (typeof arg === 'object' && arg !== null && 'id' in arg) {
    return String((arg as { id: unknown }).id) === entityId;
  }
  return false;
}

function isOpenTaskStatus(status: unknown): boolean {
  const s = String(status ?? '').toUpperCase();
  return s === 'PENDING' || s === 'IN_PROGRESS';
}

function taskMatchesFilter(record: Record<string, unknown>, args: unknown): boolean {
  if (args == null || typeof args !== 'object') return true;
  const a = args as { status?: string; category?: string };
  if (a.status) {
    if (String(record.status ?? '').toUpperCase() !== String(a.status).toUpperCase()) {
      return false;
    }
  }
  if (a.category) {
    const cat = (record.template as { category?: string } | null | undefined)?.category;
    if (String(cat ?? '').toUpperCase() !== String(a.category).toUpperCase()) {
      return false;
    }
  }
  return true;
}

/**
 * Silent tag invalidate — list UIs must use `isLoading` (first load) for
 * blocking skeletons, not `isFetching`, so socket-driven refetches never blank the page.
 */
function invalidateTagsSilent(tags: string[]) {
  const filtered = tags.filter((t) => KNOWN_TAGS.has(t));
  if (!filtered.length) return;
  store.dispatch(baseApi.util.invalidateTags(filtered as never[]));
}

function patchBareArray(
  endpoint: string,
  dataKey: string,
  action: 'ADD' | 'UPDATE' | 'DELETE',
  entityId: string,
  record: Record<string, unknown> | null,
) {
  for (const args of cachedArgs(endpoint)) {
    store.dispatch(
      apiUtil.updateQueryData(endpoint, args, (draft: Record<string, unknown>) => {
        const rows = draft[dataKey];
        if (!Array.isArray(rows)) return;
        const idx = rows.findIndex((r) => rowId(r) === entityId);
        if (action === 'DELETE') {
          if (idx >= 0) rows.splice(idx, 1);
          return;
        }
        if (!record) return;
        if (idx >= 0) {
          const prev = rows[idx] as Record<string, unknown>;
          rows[idx] = { ...prev, ...record, id: entityId };
          return;
        }
        if (action === 'ADD') {
          rows.unshift({ ...record, id: entityId });
        }
      }) as never,
    );
  }
}

function cachedArgs(endpoint: string): unknown[] {
  try {
    return apiUtil.selectCachedArgsForQuery(store.getState(), endpoint) ?? [];
  } catch {
    return [];
  }
}

function patchPaginatedList(
  endpoint: string,
  dataKey: string,
  action: 'ADD' | 'UPDATE' | 'DELETE',
  entityId: string,
  record: Record<string, unknown> | null,
) {
  for (const args of cachedArgs(endpoint)) {
    store.dispatch(
      apiUtil.updateQueryData(endpoint, args, (draft: Record<string, unknown>) => {
        const page = draft[dataKey] as
          | { rows?: unknown[]; total?: number; page?: number }
          | undefined;
        if (!page || !Array.isArray(page.rows)) return;
        const idx = page.rows.findIndex((r) => rowId(r) === entityId);
        if (action === 'DELETE') {
          if (idx < 0) return;
          page.rows.splice(idx, 1);
          if (typeof page.total === 'number' && page.total > 0) page.total -= 1;
          return;
        }
        if (!record) return;
        if (idx >= 0) {
          const prev = page.rows[idx] as Record<string, unknown>;
          page.rows[idx] = { ...prev, ...record, id: entityId };
          return;
        }
        if (action === 'ADD') {
          const pageNum = page.page ?? (args as { page?: number } | undefined)?.page ?? 1;
          const search = String((args as { search?: string } | undefined)?.search ?? '').trim();
          if (pageNum <= 1 && !search) {
            page.rows.unshift({ ...record, id: entityId });
            if (typeof page.total === 'number') page.total += 1;
            else page.total = page.rows.length;
          }
        }
      }) as never,
    );
  }
}

function patchDetail(
  endpoint: string,
  dataKey: string,
  action: 'ADD' | 'UPDATE' | 'DELETE',
  entityId: string,
  record: Record<string, unknown> | null,
) {
  for (const args of cachedArgs(endpoint)) {
    if (!argMatchesEntityId(args, entityId)) continue;
    if (action === 'DELETE') {
      store.dispatch(
        apiUtil.updateQueryData(endpoint, args, (draft: Record<string, unknown>) => {
          delete draft[dataKey];
        }) as never,
      );
      continue;
    }
    if (!record) continue;
    store.dispatch(
      apiUtil.updateQueryData(endpoint, args, (draft: Record<string, unknown>) => {
        const prev = (draft[dataKey] as Record<string, unknown> | undefined) ?? {};
        draft[dataKey] = { ...prev, ...record, id: entityId };
      }) as never,
    );
  }
}

function patchTaskArrays(
  action: 'ADD' | 'UPDATE' | 'DELETE',
  entityId: string,
  record: Record<string, unknown> | null,
) {
  let pendingDelta = 0;

  for (const args of cachedArgs('myTasks')) {
    store.dispatch(
      tenantApi.util.updateQueryData('myTasks', args as never, (draft) => {
        if (!Array.isArray(draft.myTasks)) return;
        const idx = draft.myTasks.findIndex((r) => rowId(r) === entityId);
        const prev = idx >= 0 ? (draft.myTasks[idx] as Record<string, unknown>) : null;
        const prevOpen = prev ? isOpenTaskStatus(prev.status) : false;

        if (action === 'DELETE') {
          if (idx < 0) return;
          draft.myTasks.splice(idx, 1);
          if (prevOpen) pendingDelta -= 1;
          return;
        }
        if (!record) return;

        const nextOpen = isOpenTaskStatus(record.status);
        const matches = taskMatchesFilter(record, args);

        if (idx >= 0) {
          if (!matches) {
            draft.myTasks.splice(idx, 1);
            if (prevOpen) pendingDelta -= 1;
            return;
          }
          draft.myTasks[idx] = { ...prev, ...record, id: entityId } as (typeof draft.myTasks)[number];
          if (prevOpen && !nextOpen) pendingDelta -= 1;
          if (!prevOpen && nextOpen) pendingDelta += 1;
          return;
        }

        if (action === 'ADD' && matches) {
          draft.myTasks.unshift({ ...record, id: entityId } as (typeof draft.myTasks)[number]);
          if (nextOpen) pendingDelta += 1;
        }
      }),
    );
  }

  if (pendingDelta !== 0) {
    const current = store.getState().auth.pendingTaskCount ?? 0;
    store.dispatch(setPendingTaskCount(Math.max(0, current + pendingDelta)));
  }
}

/**
 * Patch RTK list/detail caches from an org-data-changed socket payload.
 * Patch-first: never invalidate when we can no-op or patch. Silent invalidate
 * only for unknown entity types (aggregates) — UIs must not block on isFetching.
 */
export function applyOrgDataChanged(payload: OrgDataChangedPayload | undefined) {
  if (!payload) return;

  const entityType = String(payload.entityType ?? '')
    .trim()
    .toLowerCase();
  const action = normalizeAction(payload.action);
  const entityId =
    payload.entityId != null && payload.entityId !== ''
      ? String(payload.entityId)
      : payload.record && typeof payload.record === 'object'
        ? rowId(payload.record)
        : '';

  const config = ENTITY_CACHE[entityType];
  if (!config || !action) {
    // Unknown entity / missing action — silent refetch only (no full-page loader).
    invalidateTagsSilent(payload.tags ?? config?.tags ?? []);
    return;
  }

  const record =
    payload.record != null && typeof payload.record === 'object'
      ? (payload.record as Record<string, unknown>)
      : null;

  // Known entity but no patchable payload → no-op (do not invalidate + spinner).
  if (action !== 'DELETE' && !record) {
    return;
  }

  if (!entityId && action !== 'ADD') {
    return;
  }

  const id = entityId || (record ? rowId(record) : '');
  if (!id) {
    return;
  }

  for (const list of config.lists ?? []) {
    patchPaginatedList(list.endpoint, list.dataKey, action, id, record);
  }
  for (const detail of config.details ?? []) {
    patchDetail(detail.endpoint, detail.dataKey, action, id, record);
  }
  for (const arr of config.arrays ?? []) {
    if (arr.endpoint === 'myTasks' || arr.dataKey === 'myTasks') {
      patchTaskArrays(action, id, record);
    } else {
      patchBareArray(arr.endpoint, arr.dataKey, action, id, record);
    }
  }

  bumpDashboardCounts(entityType, action, record);

  // Settings billing: quiet background refetch of invoice/plan queries (no full-page loader).
  if (entityType === 'systemnotification' || entityType === 'system-notification') {
    invalidateTagsSilent(['Billing']);
  }
}

/** Adjust Home dashboard counters in place on ADD/DELETE (no refetch). */
function bumpDashboardCounts(
  entityType: string,
  action: 'ADD' | 'UPDATE' | 'DELETE',
  record: Record<string, unknown> | null,
) {
  if (action !== 'ADD' && action !== 'DELETE') return;
  const delta = action === 'ADD' ? 1 : -1;

  let field: keyof {
    customers: number;
    vendors: number;
    products: number;
    salesOrders: number;
    purchaseOrders: number;
    totalProcurement: number;
  } | null = null;

  if (entityType === 'customer') field = 'customers';
  else if (entityType === 'vendor') field = 'vendors';
  else if (entityType === 'product') field = 'products';
  else if (entityType === 'procurement' || entityType === 'proc') {
    const kind = String(record?.procType ?? record?.kind ?? record?.type ?? '').toUpperCase();
    if (kind.includes('SALE')) field = 'salesOrders';
    else if (kind.includes('PURCHASE')) field = 'purchaseOrders';
    else field = 'totalProcurement';
  } else {
    return;
  }

  try {
    store.dispatch(
      tenantApi.util.updateQueryData('dashboardStats', undefined, (draft) => {
        if (!draft) return;
        if (field === 'totalProcurement') {
          draft.totalProcurement = Math.max(0, (draft.totalProcurement ?? 0) + delta);
          return;
        }
        if (field === 'salesOrders') {
          draft.salesOrders = Math.max(0, (draft.salesOrders ?? 0) + delta);
          draft.totalProcurement = Math.max(0, (draft.totalProcurement ?? 0) + delta);
          return;
        }
        if (field === 'purchaseOrders') {
          draft.purchaseOrders = Math.max(0, (draft.purchaseOrders ?? 0) + delta);
          draft.totalProcurement = Math.max(0, (draft.totalProcurement ?? 0) + delta);
          return;
        }
        draft[field] = Math.max(0, (draft[field] ?? 0) + delta);
      }),
    );
  } catch {
    // Cache may be empty — ignore.
  }
}
